/**
 * Color-coded "execution kind" chip used by both the migration-task
 * detail panel (editable) and the chart's TaskNode (read-only).
 *
 * Keeps the visual contract identical to the WBS area / vendor /
 * assignee chip family (rounded-full, bg-* + text-* color pair) so
 * users see one shared chip language across both views.
 *
 * Color mapping (per UI spec PR #32):
 *   - manual    → 赤  (rose)   "手作業"
 *   - automated → 緑  (emerald) "自動化"
 *   - hybrid    → 黄  (amber)  "ハイブリッド"
 *
 * Width contract: when interactive, the trigger reserves enough min
 * width to fit "ハイブリッド" (the longest label) so swapping the value
 * from "手作業" → "ハイブリッド" doesn't shift surrounding layout. We
 * achieve that with a hidden "ハイブリッド" inside a CSS grid cell that
 * the actual label overlays — every variant computes the same width.
 *
 * Padding contract: the chip uses the same `px-1.5 py-0 text-[11px]`
 * footprint as `<AssigneeChip size="sm" />`, so the DAG task card and
 * any other surface that stacks both chip families together visually
 * aligns. The shared sizing lives in `EXECUTION_KIND_CHIP_SIZE_CLASSES`
 * below — bumping it once changes both the read-only and the editable
 * trigger together, and the unit test pins it against AssigneeChip.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ExecutionType } from '@mct/shared';
import { cn } from '../../lib/utils';

/** Display labels (ja) for each kind. Kept exported so tests can pin
 *  them and so the chart card / detail panel agree on copy. */
export const EXECUTION_KIND_LABEL: Record<ExecutionType, string> = {
  manual: '手作業',
  automated: '自動化',
  hybrid: 'ハイブリッド',
};

/** Order chosen so the dropdown reads left-to-right by frequency
 *  (manual is by far the most common in the migration workflow). */
export const EXECUTION_KIND_ORDER: readonly ExecutionType[] = [
  'manual',
  'automated',
  'hybrid',
];

/** Tailwind classes per kind. Chip BG+text are paired so swapping the
 *  value flips both at once. Exported for tests + chart card. */
export const EXECUTION_KIND_CHIP_CLASSES: Record<ExecutionType, string> = {
  manual: 'bg-rose-100 text-rose-700',
  automated: 'bg-emerald-100 text-emerald-700',
  hybrid: 'bg-amber-100 text-amber-700',
};

/** The widest label — used both as the dropdown's invisible width
 *  spacer and exported so unit tests can pin the contract that the
 *  trigger aligns to this string. */
export const EXECUTION_KIND_WIDEST_LABEL = EXECUTION_KIND_LABEL.hybrid;

/**
 * Shared sizing classes — kept in lockstep with `AssigneeChip`'s `sm`
 * variant (`px-1.5 py-0 text-[11px]`) so the DAG task card lines the
 * two chips up at the same height. Exported so the unit test can pin
 * the contract against AssigneeChip without re-deriving the string.
 */
export const EXECUTION_KIND_CHIP_SIZE_CLASSES = 'px-1.5 py-0 text-[11px]';

interface BaseProps {
  value: ExecutionType;
  /** Optional extra classes merged onto the trigger element. */
  className?: string;
  /** Native `title` attribute; defaults to the label. */
  title?: string;
}

interface ReadOnlyProps extends BaseProps {
  onChange?: undefined;
}

interface EditableProps extends BaseProps {
  onChange: (next: ExecutionType) => void | Promise<void>;
}

export type ExecutionKindChipProps = ReadOnlyProps | EditableProps;

/**
 * Render the chip. With `onChange` it's an editable dropdown trigger;
 * without it, a static `<span>` for read-only surfaces (DAG card).
 *
 * Both variants share the same fixed-width contract — the rendered
 * element grid-stacks an invisible "ハイブリッド" so the visible label
 * never resizes the trigger when the user toggles between values.
 */
export function ExecutionKindChip(props: ExecutionKindChipProps): JSX.Element {
  const { value, className, title } = props;
  const editable = 'onChange' in props && typeof props.onChange === 'function';
  const label = EXECUTION_KIND_LABEL[value];
  const colorClasses = EXECUTION_KIND_CHIP_CLASSES[value];

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Outside-click + Esc to close, mirroring the WBS KindChip popover.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Defer one tick so the click that opened the popover doesn't
    // immediately close it via the document listener.
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onClick);
    }, 0);
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Inner label block — uses a 1-cell CSS grid where an invisible copy
  // of the widest label reserves width and the visible label overlays
  // it. This is the "fixed width = ハイブリッド" contract the spec
  // calls out: changing the value never reflows the surrounding row.
  const labelBlock = (
    <span className="grid grid-cols-1 items-center justify-items-center">
      <span
        aria-hidden
        className="invisible col-start-1 row-start-1 whitespace-nowrap"
      >
        {EXECUTION_KIND_WIDEST_LABEL}
      </span>
      <span className="col-start-1 row-start-1 whitespace-nowrap">
        {label}
      </span>
    </span>
  );

  if (!editable) {
    return (
      <span
        data-testid="execution-kind-chip"
        data-kind={value}
        title={title ?? label}
        className={cn(
          'inline-flex items-center justify-center rounded-full font-medium leading-5',
          EXECUTION_KIND_CHIP_SIZE_CLASSES,
          colorClasses,
          className,
        )}
      >
        {labelBlock}
      </span>
    );
  }

  const onChange = props.onChange;
  const onSelect = async (next: ExecutionType) => {
    setOpen(false);
    if (next === value) return;
    try {
      await onChange(next);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[execution-kind-chip] onChange failed', err);
    }
  };

  return (
    <div
      ref={wrapRef}
      className="relative inline-block"
      data-testid="execution-kind-chip-root"
    >
      <button
        type="button"
        data-testid="execution-kind-chip-trigger"
        data-kind={value}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title ?? '実行方式を変更'}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1 rounded-full font-medium leading-5 transition-colors',
          EXECUTION_KIND_CHIP_SIZE_CLASSES,
          colorClasses,
          'hover:brightness-95',
          className,
        )}
      >
        {labelBlock}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open ? (
        <div
          role="menu"
          aria-label="実行方式"
          data-testid="execution-kind-chip-popover"
          className="absolute left-0 top-full z-40 mt-1 min-w-fit overflow-hidden whitespace-nowrap rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {EXECUTION_KIND_ORDER.map((kind) => {
            const isCurrent = kind === value;
            return (
              <button
                key={kind}
                type="button"
                role="menuitemradio"
                aria-checked={isCurrent}
                onClick={() => void onSelect(kind)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1 text-left text-xs hover:bg-slate-50',
                  isCurrent ? 'bg-slate-50 font-semibold' : '',
                )}
              >
                <span
                  className={cn(
                    'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-medium',
                    EXECUTION_KIND_CHIP_CLASSES[kind],
                  )}
                >
                  {EXECUTION_KIND_LABEL[kind]}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
